import { Operation } from '../api/types';
import { celebrateBig, celebrateSmall } from './confetti';
import { CRIT_PHRASES, INCOME_PHRASES } from './phrases';
import { createdAtLocalDay } from './recording-streak';

/**
 * Монеты — производная от операций, без хранения на сервере:
 * крит детерминирован hash'ем id операции, поэтому сумма монет
 * стабильно пересчитывается на любом устройстве.
 */

export const COIN_RECORD = 10;
export const COIN_CRIT = 25;
export const COIN_INCOME = 50;
const CRIT_CHANCE = 18; // %

/** FNV-1a — быстрый стабильный hash строки. */
export function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export interface OpReward {
  coins: number;
  crit: boolean;
  income: boolean;
  phrase: string | null;
}

export function rewardForOp(op: { id: string; type: string }): OpReward {
  if (op.type === 'Доход') {
    return {
      coins: COIN_INCOME,
      crit: false,
      income: true,
      phrase: INCOME_PHRASES[hashStr(op.id + 'i') % INCOME_PHRASES.length],
    };
  }
  const crit = hashStr(op.id) % 100 < CRIT_CHANCE;
  if (crit) {
    return {
      coins: COIN_CRIT,
      crit: true,
      income: false,
      phrase: CRIT_PHRASES[hashStr(op.id + 'c') % CRIT_PHRASES.length],
    };
  }
  return { coins: COIN_RECORD, crit: false, income: false, phrase: null };
}

/** Показать награду за сохранённую операцию: confetti + плашка-гроссбух. */
export function celebrateReward(
  r: OpReward,
  toast: { reward: (t: { icon?: 'coin' | 'feather'; title: string; amount?: string; crit?: boolean; sub?: string }) => void },
  opts: { title?: string; sub?: string } = {},
): void {
  const sub = [r.phrase, opts.sub].filter(Boolean).join(' · ') || undefined;
  if (r.income) {
    celebrateBig();
    toast.reward({ icon: 'feather', title: opts.title ?? 'Доход записан', amount: `+${r.coins}`, sub });
  } else if (r.crit) {
    celebrateSmall();
    toast.reward({ title: 'Крит!', crit: true, amount: `+${r.coins}`, sub });
  } else {
    toast.reward({ icon: 'feather', title: opts.title ?? 'Записано', amount: `+${r.coins}`, sub });
  }
}

/**
 * Монеты сезона = сумма наград за операции, ЗАПИСАННЫЕ в текущем
 * календарном месяце (по created_at). Импортированные не считаются.
 * Сезон обнуляется сам собой 1-го числа.
 */
export function seasonCoins(operations: Operation[], now: Date = new Date()): number {
  const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  let total = 0;
  for (const op of operations) {
    if (op.id.startsWith('imp_')) continue;
    const day = createdAtLocalDay(op.created_at);
    if (!day || !day.startsWith(monthKey)) continue;
    total += rewardForOp(op).coins;
  }
  return total;
}
